import React, { useRef, useEffect, useLayoutEffect, FC } from 'react';
import { CandidateProfileHeader } from '@src/containers/CandidateProfileHeader';
import { MessageEntryArea } from '@src/containers/MessageEntryArea';
import { Messages } from '@src/containers/Messages';
import { hideIntercom, showIntercom } from '@src/intercom';
import { CircularLoading, Size } from '@src/components/CircularLoading';
import { ClosedChatMessageSection } from '@src/components/ClosedChatMessageSection';
import { CandidateProfileProps } from './props';
import './styles.scss';

export const CandidateProfile: FC<CandidateProfileProps> = ({
  application,
  actions,
  isSending,
  metricsContext,
  message,
  onSend,
  onAccept,
  onReject,
  onClose,
  onChatReopen,
  onChatClose,
  onInputChange,
}) => {
  const profileBodyRef = useRef<HTMLDivElement>(null);

  const threadId = application?.messageThread?.id;
  const openingId = application?.opening?.id;
  const applicationId = application?.id;
  const messages = application?.messageThread?.messages.result || [];
  const user = application?.messageThread?.user;
  const countryCode = user?.phone?.country?.dialCode;
  const phoneNumber = user?.phone?.number;
  const userPhone = countryCode && phoneNumber ? `+${countryCode} ${phoneNumber}` : undefined;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      switch (event.code) {
        case 'Escape':
          event.preventDefault();

          event.stopPropagation();

          onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    hideIntercom();

    return () => {
      document.removeEventListener('keydown', onKeyDown);

      showIntercom();
    };
  }, []);

  useLayoutEffect(() => {
    const scrollToBottom = (): void => {
      if (profileBodyRef.current) {
        profileBodyRef.current.scrollTop = profileBodyRef.current.scrollHeight;
      }
    };

    scrollToBottom();
  }, [threadId, messages.length]);

  const onWheel = (event: WheelEvent): void => {
    if (profileBodyRef && profileBodyRef.current) {
      const { scrollHeight, scrollTop, clientHeight } = profileBodyRef.current;

      const hasReachedBottom = scrollTop + clientHeight >= scrollHeight && event.deltaY > 0;

      const hasReachedTop = scrollTop <= 0 && event.deltaY < 0;

      if (hasReachedBottom || hasReachedTop) {
        event = event || window.event;

        if (event.preventDefault) {
          event.preventDefault();
        }

        event.returnValue = false;
      }
    }
  };

  const handleAccept = (): void => {
    onAccept();
  };

  const handleReject = (): void => {
    onReject();
  };

  if (!application || !application.messageThread) {
    return (
      <div className="candidate-profile__loading">
        <CircularLoading size={Size.MEDIUM} />
      </div>
    );
  }

  const isChatDisabled = isSending || !message.trim().length;

  return (
    <>
      <CandidateProfileHeader
        actions={actions}
        applicationId={applicationId}
        className="candidate-profile__header"
        metricsContext={metricsContext}
        onClose={onClose}
        openingId={openingId}
        user={application.messageThread?.user}
      />

      <div className="candidate-profile__body">
        <div
          className="candidate-profile__content"
          // Add event listener on wheel, to prevent body scroll if cursor
          // is inside candidate profile chat window
          onMouseEnter={() => profileBodyRef.current?.addEventListener('wheel', onWheel)}
          onMouseLeave={() => profileBodyRef.current?.removeEventListener('wheel', onWheel)}
          ref={profileBodyRef}
        >
          <Messages
            className="candidate-profile__messages"
            messages={messages}
            openingTitle={application?.opening?.title || undefined}
            user={application.messageThread?.user}
          />
        </div>

        {user?.state !== 'draft' ? (
          <MessageEntryArea
            isDisabled={isChatDisabled}
            message={message}
            messageThread={application.messageThread}
            metricsContext={metricsContext}
            onAccept={handleAccept}
            onChange={onInputChange}
            onClose={onChatClose}
            onReject={handleReject}
            onReopen={onChatReopen}
            onSend={onSend}
          />
        ) : (
          <ClosedChatMessageSection
            email={user.email || undefined}
            phone={userPhone}
            userName={user.name || undefined}
          />
        )}
      </div>
    </>
  );
};
